import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrderVisit } from '@/hooks/useOrderVisit';
import { getDetailOrderId } from '@/utils/getDetailOrderId';
import { getOrderId, isMobile } from '@/biz/tool';
import { shouldRequireCreatorAgreement } from '@/biz/creatorAgreement';
import toast from 'react-hot-toast';
import WebModal from '../Modal';
import BottomDrawer from '../BottomDrawer';
import type { StripeAddressElement } from '@stripe/stripe-js';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useTranslation } from 'react-i18next';
import { containsProhibitedPOBox, validateRFC } from './utils';
import SelectProductContent from './SelectProductContent';
import ModalContent from './ModalContent';
import AgreementContent from './AgreementContent';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import { isCreatorOrderV2ApiError } from '@/apis/creatorOrderV2/errors';
import {
  assertCreatorDeliveryAction,
  handleCreatorDeliveryWriteError
} from '@/biz/multiPlatformCollaboration/creatorDeliveryActions';
import { v4 as uuidv4 } from 'uuid';
import { useCreatorDeliveryTimelineStore } from '@/hooks/useCreatorDeliveryTimelineStore';
import { getCreatorTimelineContentNodes } from '@/biz/multiPlatformCollaboration/creatorDeliveryTimeline';
import { ActionsStatusEnum } from '../../home/config';
import { showCreatorDeliveryCompletion } from '@/hooks/creatorDeliveryCompletion';

interface Props {
  orderId: string;
}

type AddressFlowStep = 'agreement' | 'product' | 'address';

const ModalAddress: React.FC<Props> = ({ orderId }) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { isShowAddressModal, setShowAddressModal, isAgree } =
    useWebModalStore();
  const [complete, setComplete] = useState(false);
  const requestRef = useRef({ pending: false, fingerprint: '', key: '' });
  const [stripeComplete, setStripeComplete] = useState(false);
  const [poBoxInvalid, setPoBoxInvalid] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const addressElementRef = useRef<StripeAddressElement | null>(null);
  const showRfcValidationRef = useRef<(() => void) | null>(null);
  const isCurrentOrderVisit = useOrderVisit(orderId);
  const { product, order } = detailData;
  const shouldShowCreatorAgreement = shouldRequireCreatorAgreement(
    detailData?.order?.appliedAt
  );
  const [creatorAgreementError, setCreatorAgreementError] = useState(false);
  const [addressFlowStep, setAddressFlowStep] =
    useState<AddressFlowStep>('agreement');
  const [selectedProductInThisFlow, setSelectedProductInThisFlow] =
    useState(false);
  const [agreementChecked, setAgreementChecked] = useState<[boolean, boolean]>([
    false,
    false
  ]);
  const [addAddressToAccount, setAddAddressToAccount] = useState(false);
  const [skuListData, setSkuListData] = useState<any[]>(
    (order?.skuList?.length > 0 ? order?.skuList : product?.skuList) || []
  );
  useEffect(() => {
    const currentSkuList =
      (order?.skuList?.length > 0 ? order?.skuList : product?.skuList) || [];
    setSkuListData(currentSkuList);
  }, [order?.skuList, product?.skuList]);

  useEffect(() => {
    if (!isShowAddressModal) return;
    setAddressFlowStep('agreement');
    setSelectedProductInThisFlow(false);
    setAgreementChecked([false, false]);
    setCreatorAgreementError(false);
  }, [isShowAddressModal]);

  const handleSubmit = async () => {
    if (!isCurrentOrderVisit() || getDetailOrderId(order) !== orderId) return;
    const request = requestRef.current;
    if (request.pending) return;
    if (!complete) {
      // Stripe AddressElement: getValue() validates and shows errors under each field
      await addressElementRef.current?.getValue();
      showRfcValidationRef.current?.();
      return;
    }
    if (shouldShowCreatorAgreement && !isAgree) {
      setCreatorAgreementError(true);
      return;
    }
    try {
      request.pending = true;
      const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
      if (containsProhibitedPOBox(addressText)) {
        toast.error(
          t('modal.address.no_po_box.error', {
            defaultValue:
              'Please provide a physical shipping address (no PO Boxes).'
          })
        );
        return;
      }
      assertCreatorDeliveryAction(orderId, 'SUBMIT_ADDRESS');
      const timelineState = useCreatorDeliveryTimelineStore.getState();
      const addressNode = getCreatorTimelineContentNodes(
        timelineState.timeline
      ).find(
        (node) =>
          node.code === 'ADDRESS' && node.actions.includes('SUBMIT_ADDRESS')
      );
      const body = { addressInfo: address, saveToAccount: addAddressToAccount };
      const fingerprint = JSON.stringify({ orderId, body });
      if (request.fingerprint !== fingerprint) {
        request.fingerprint = fingerprint;
        request.key = uuidv4();
      }
      await creatorOrderV2Api.address(orderId, body, request.key);
      if (!isCurrentOrderVisit()) return;
      // Snapshot the completed address stage before refresh advances the timeline.
      const now =
        Date.parse(timelineState.timeline?.serverNow || '') +
        Math.max(0, Date.now() - timelineState.receivedAt);
      const dueAt = addressNode?.deadline?.dueAt;
      const bonus =
        detailData?.order?.interestedPriceFields?.[0]?.bonusAmount > 0 &&
        (addressNode?.data.appeal?.status === 'approved' ||
          Boolean(dueAt && Date.parse(dueAt) > now));
      await reQueryDetailData({ orderId });
      if (!isCurrentOrderVisit()) return;
      setShowAddressModal(false);
      request.fingerprint = '';
      showCreatorDeliveryCompletion({
        orderId,
        node: ActionsStatusEnum.PendingCreatorAddress,
        mode: bonus ? 'bonus' : 'node',
        isCurrentVisit: isCurrentOrderVisit,
        refresh: () => reQueryDetailData({ orderId })
      });
    } catch (error: unknown) {
      if (!isCurrentOrderVisit()) return;
      handleCreatorDeliveryWriteError(error);
      if (isCreatorOrderV2ApiError(error)) request.fingerprint = '';
      if (
        isCreatorOrderV2ApiError(error) &&
        (error.recoveryAction === 'REFRESH_WITHOUT_REPLAY' ||
          error.recoveryAction === 'REFRESH_DELIVERY_REVISION' ||
          error.code === 47002)
      ) {
        await reQueryDetailData({ orderId });
        if (!isCurrentOrderVisit()) return;
        toast.error(
          t('multi_platform.delivery.state_conflict', {
            defaultValue:
              'The collaboration status changed. The latest details have been loaded and nothing was resubmitted.'
          })
        );
        return;
      }
      toast.error(
        (error instanceof Error ? error.message : '') ||
          t('modal.address.submit.error', {
            defaultValue: 'Submission failed.'
          })
      );
    } finally {
      request.pending = false;
    }
  };

  const needsProductSelection =
    !order?.skuList?.length && Boolean(product?.skuList?.length);
  // Saving SKU refreshes order.skuList. Keep the completed step in this open
  // flow, but skip it when reopening an order whose selection is already saved.
  const hasProductSelection =
    selectedProductInThisFlow || needsProductSelection;
  const handleAgreementContinue = () => {
    setSelectedProductInThisFlow(needsProductSelection);
    setAddressFlowStep(needsProductSelection ? 'product' : 'address');
  };

  const handleAddressChange = (event: any) => {
    const { complete, value, rfcValue } = event;

    const { address, name, phone } = value;
    setAddress({
      ...address,
      name,
      phone,
      rfcValue: address?.country === 'MX' ? rfcValue : ''
    });
    setStripeComplete(complete);

    const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
    const hasProhibitedPOBox = containsProhibitedPOBox(addressText);
    if (hasProhibitedPOBox) {
      // 每次检测到禁止内容都提示，避免从一种禁止写法改成另一种时用户看不到报错
      toast.error(
        t('modal.address.no_po_box.error', {
          defaultValue:
            'Please provide a physical shipping address (no PO Boxes).'
        })
      );
      setPoBoxInvalid(true);
      setComplete(false);
      return;
    }
    if (poBoxInvalid) {
      setPoBoxInvalid(false);
    }

    if (address?.country === 'MX' && !validateRFC(rfcValue)) {
      setComplete(false);
      return;
    }
    setComplete(complete);
  };

  const addressFlowContent =
    addressFlowStep === 'agreement' ? (
      <AgreementContent
        checked={agreementChecked}
        onCheckedChange={setAgreementChecked}
        onCancel={() => setShowAddressModal(false)}
        onContinue={handleAgreementContinue}
        hasProductSelection={hasProductSelection}
        shouldShowCreatorAgreement={shouldShowCreatorAgreement}
        creatorAgreementError={creatorAgreementError}
        setCreatorAgreementError={setCreatorAgreementError}
      />
    ) : addressFlowStep === 'product' ? (
      <SelectProductContent
        autoFocus={isShowAddressModal}
        skuListData={skuListData}
        setSkuListData={setSkuListData}
        setShowAddressModal={setShowAddressModal}
        onContinue={() => setAddressFlowStep('address')}
      />
    ) : (
      <ModalContent
        autoFocus={isShowAddressModal}
        handleAddressChange={handleAddressChange}
        addressInfo={address}
        complete={stripeComplete}
        addressElementRef={addressElementRef}
        showRfcValidationRef={showRfcValidationRef}
        addAddressToAccount={addAddressToAccount}
        setAddAddressToAccount={setAddAddressToAccount}
        hasProductSelection={hasProductSelection}
      />
    );

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          isShowHeader={false}
          isShowFooter={addressFlowStep === 'address'}
          isShowBack={false}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
        >
          {addressFlowContent}
        </BottomDrawer>
      ) : (
        <WebModal
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          isShowHeader={false}
          isShowFooter={addressFlowStep === 'address'}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
          isShowBack={false}
        >
          {addressFlowContent}
        </WebModal>
      )}
    </>
  );
};

// The modal is global; remount its form when the route changes so addresses and
// validation from one order cannot be submitted for another.
export default function OrderAddressModal() {
  const location = useLocation();
  const orderId = getOrderId();
  useLayoutEffect(
    () => () => {
      useWebModalStore.getState().setShowAddressModal(false);
    },
    [location.pathname]
  );
  return <ModalAddress key={orderId} orderId={orderId} />;
}
